<template>
  <v-container>
    <!-- Header band -->
    <div class="topbar">
      <div class="topbar__row">
        <v-btn
          icon
          variant="text"
          :title="$filters.capitalize($t('machine.back_solar'))"
          :to="{ name: 'solar_production' }"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div :class="['topbar__badge', status ? (status.time ? 'topbar__badge--on' : 'topbar__badge--off') : '']">
          <v-icon size="22">mdi-solar-panel-large</v-icon>
        </div>
        <div class="topbar__title min-width-0">
          <div class="topbar__name">
            {{ producerName || $filters.capitalize($t("production.name")) }}
          </div>
          <div class="topbar__sub">
            <template v-if="producer">
              {{ producer.power_station_name }}
              <template v-if="producer.capacity_solar_panel"> &middot; {{ producer.capacity_solar_panel }} {{ $t("unity.kw") }}c</template>
            </template>
            <template v-if="lastRefresh"> &middot; {{ $t("production.last_refresh") }} {{ $filters.formatTime(lastRefresh) }}</template>
          </div>
        </div>
        <div class="topbar__actions">
          <v-btn-toggle
            v-model="range"
            mandatory
            density="compact"
            variant="outlined"
            color="primary"
            class="range-toggle"
            @update:model-value="loadTelemetries"
          >
            <v-btn v-for="r in ranges" :key="r.value" :value="r.value" size="small">{{ r.label }}</v-btn>
          </v-btn-toggle>
          <v-btn
            v-if="producer && producer.power_station_id"
            variant="tonal"
            size="small"
            prepend-icon="mdi-view-dashboard-outline"
            :to="{ name: 'machine_solar_producer_view', query: { power_station_id: producer.power_station_id } }"
          >
            {{ $filters.capitalize($t("machine.station")) }}
          </v-btn>
          <v-btn
            icon
            variant="tonal"
            color="primary"
            size="small"
            :loading="loading"
            :title="$filters.capitalize($t('production.refresh'))"
            @click="load"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" height="2" class="topbar__progress"></v-progress-linear>
    </div>

    <template v-if="producer_id">
      <!-- Status KPIs -->
      <div v-if="status" class="kpi-grid">
        <v-card flat class="kpi kpi--amber">
          <div class="kpi__badge"><v-icon size="20">mdi-flash</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("production.power")) }}</div>
            <div class="kpi__value" :class="{ 'text-warning': isBelowExpected(status) }">
              {{ $filters.formatFloat(status.power) }}
              <span class="kpi__unit">{{ $t("unity.kw") }}</span>
            </div>
            <div class="kpi__hint">
              <template v-if="loadFactor(status) !== null">{{ $t("production.load_factor") }} {{ Math.round(loadFactor(status) * 100) }} %</template>
            </div>
          </div>
        </v-card>
        <v-card flat class="kpi kpi--teal">
          <div class="kpi__badge"><v-icon size="20">mdi-transmission-tower-export</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("production.injection")) }}</div>
            <div class="kpi__value">
              <template v-if="hasValue(status.injection)">{{ $filters.formatFloat(status.injection) }} <span class="kpi__unit">{{ $t("unity.kw") }}</span></template>
              <span v-else class="text-disabled">&ndash;</span>
            </div>
            <div class="kpi__hint">
              <template v-if="hasValue(status.performance_ratio_actual_month_min)">
                {{ $t("production.expected") }} {{ $filters.formatFloat(status.performance_ratio_actual_month_min) }} – {{ $filters.formatFloat(status.performance_ratio_actual_month_max) }} {{ $t("unity.kw") }}
              </template>
            </div>
          </div>
        </v-card>
        <v-card flat class="kpi kpi--yellow">
          <div class="kpi__badge"><v-icon size="20">mdi-white-balance-sunny</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("production.irradiance")) }}</div>
            <div class="kpi__value">
              <template v-if="hasValue(status.radiance_vert)">{{ $filters.formatFloat(status.radiance_vert) }} <span class="kpi__unit">{{ $t("unity.wm2") }}</span></template>
              <span v-else class="text-disabled">&ndash;</span>
            </div>
            <div class="kpi__hint">
              {{ $t("telemetry.radiance_horizontal") }}
              <template v-if="hasValue(status.radiance_hor)">{{ $filters.formatFloat(status.radiance_hor) }} {{ $t("unity.wm2") }}</template>
              <template v-else>–</template>
            </div>
          </div>
        </v-card>
        <v-card flat class="kpi kpi--blue">
          <div class="kpi__badge"><v-icon size="20">mdi-clock-outline</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("production.last_data")) }}</div>
            <div class="kpi__value kpi__value--small">
              <span v-if="status.time" class="fresh-chip" :class="freshnessClass(status.time) || 'fresh'">{{ relativeTime(status.time) }}</span>
              <span v-else class="fresh-chip over_5h">{{ $t("production.no_telemetry") }}</span>
            </div>
            <div class="kpi__hint">
              <template v-if="status.time">{{ $filters.formatDateTime(status.time) }}</template>
            </div>
          </div>
        </v-card>
      </div>

      <!-- Grafana panels -->
      <v-row dense class="mt-1">
        <v-col cols="12" lg="8">
          <v-card flat class="graf-card">
            <div class="graf-card__head">
              <v-icon size="small" class="mr-2" color="warning">mdi-chart-line</v-icon>
              <span class="text-subtitle-2 font-weight-bold">{{ $filters.capitalize($t("machine.expected_chart")) }}</span>
            </div>
            <GrafanaFrame
              v-if="GRAFANA_EMBED_ENABLED"
              class="graf-frame"
              :src="grafanaSolarProducerPanelUrl(producer_id, GRAFANA_SOLAR_PANELS.producer_expected, range)"
              :href="grafanaSolarProducerUrl(producer_id, range)"
              :title="$t('machine.expected_chart')"
              :hint="$t('machine.open_grafana')"
            ></GrafanaFrame>
            <a v-else :href="grafanaSolarProducerUrl(producer_id, range)" target="_blank" rel="noopener" class="graf-placeholder">
              <v-icon size="32" color="primary">mdi-chart-line</v-icon>
              <span>{{ $t("machine.open_grafana") }}</span>
              <span class="text-caption text-medium-emphasis">{{ $t("machine.embed_disabled") }}</span>
            </a>
          </v-card>
        </v-col>
        <v-col cols="12" lg="4">
          <v-card flat class="graf-card">
            <div class="graf-card__head">
              <v-icon size="small" class="mr-2" color="warning">mdi-white-balance-sunny</v-icon>
              <span class="text-subtitle-2 font-weight-bold">{{ $filters.capitalize($t("machine.irradiance_chart")) }}</span>
            </div>
            <GrafanaFrame
              v-if="GRAFANA_EMBED_ENABLED"
              class="graf-frame"
              :src="grafanaSolarProducerPanelUrl(producer_id, GRAFANA_SOLAR_PANELS.producer_irradiance, range)"
              :href="grafanaSolarProducerUrl(producer_id, range)"
              :title="$t('machine.irradiance_chart')"
              :hint="$t('machine.open_grafana')"
            ></GrafanaFrame>
            <a v-else :href="grafanaSolarProducerUrl(producer_id, range)" target="_blank" rel="noopener" class="graf-placeholder">
              <v-icon size="32" color="primary">mdi-white-balance-sunny</v-icon>
              <span>{{ $t("machine.open_grafana") }}</span>
            </a>
          </v-card>
        </v-col>
      </v-row>

      <!-- Latest telemetries -->
      <v-card flat class="table-card">
        <div class="table-card__head">
          <v-icon size="small" class="mr-2" color="primary">mdi-table</v-icon>
          <span class="text-subtitle-1 font-weight-bold">{{ $filters.capitalize($t("machine.latest_telemetries")) }}</span>
          <v-chip size="x-small" variant="tonal" class="ml-2">{{ telemetries.length }}</v-chip>
          <span class="text-caption text-medium-emphasis ml-2">{{ rangeLabel }}</span>
          <v-spacer></v-spacer>
          <v-btn
            size="small"
            variant="text"
            color="primary"
            prepend-icon="mdi-download"
            @click="showExport = true"
          >
            {{ $filters.capitalize($t("export")) }}
          </v-btn>
        </div>
        <v-data-table
          class="tel-table"
          :headers="headers"
          :items="telemetries"
          :items-per-page="20"
          :loading="telemetriesLoading"
          density="compact"
          :no-data-text="$filters.capitalize($t('production.no_telemetry'))"
        >
          <template v-slot:[`item.time`]="{ item }">
            <span class="text-no-wrap">{{ $filters.formatDateTime(item.time) }}</span>
          </template>
          <template v-slot:[`item.power`]="{ item }">
            <span class="font-weight-medium">{{ $filters.formatFloat(item.power) }}</span>
            <span class="text-caption text-medium-emphasis"> {{ $t("unity.kw") }}</span>
          </template>
          <template v-slot:[`item.injection`]="{ item }">
            <template v-if="hasValue(item.injection)">{{ $filters.formatFloat(item.injection) }} <span class="text-caption text-medium-emphasis">{{ $t("unity.kw") }}</span></template>
            <span v-else class="text-disabled">&ndash;</span>
          </template>
          <template v-slot:[`item.radiance_vert`]="{ item }">
            <template v-if="hasValue(item.radiance_vert)">{{ $filters.formatFloat(item.radiance_vert) }} <span class="text-caption text-medium-emphasis">{{ $t("unity.wm2") }}</span></template>
            <span v-else class="text-disabled">&ndash;</span>
          </template>
          <template v-slot:[`item.radiance_hor`]="{ item }">
            <template v-if="hasValue(item.radiance_hor)">{{ $filters.formatFloat(item.radiance_hor) }} <span class="text-caption text-medium-emphasis">{{ $t("unity.wm2") }}</span></template>
            <span v-else class="text-disabled">&ndash;</span>
          </template>
        </v-data-table>
      </v-card>

      <ExportRangeDialog
        v-model="showExport"
        unit="day"
        :title="$filters.capitalize($t('production.export_hint'))"
        :subtitle="producerName"
        @export="download"
      ></ExportRangeDialog>
    </template>

    <v-card v-else flat class="empty-card text-medium-emphasis text-body-2">
      <v-icon size="36" class="mb-2" color="grey">mdi-solar-panel</v-icon>
      <div>{{ $filters.capitalize($t("machine.producer_not_found")) }}</div>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import {
  isNone,
  grafanaSolarProducerUrl,
  grafanaSolarProducerPanelUrl,
  GRAFANA_SOLAR_PANELS,
  GRAFANA_EMBED_ENABLED
} from "@/functions.js";
import utils from "@/mixins/utils";
import GrafanaFrame from "@/components/GrafanaFrame.vue";
import ExportRangeDialog from "@/components/ExportRangeDialog.vue";

const RANGE_HOURS = { "now-24h": 24, "now-7d": 24 * 7, "now-30d": 24 * 30 };

export default {
  components: { GrafanaFrame, ExportRangeDialog },
  mixins: [utils],

  computed: {
    producer_id() {
      const id = Number(this.$route.query.producer_id);
      return Number.isFinite(id) && id > 0 ? id : null;
    },

    producer() {
      if (this.status && this.status.producer) {
        return this.status.producer;
      }
      if (this.telemetries.length && this.telemetries[0].producer) {
        return this.telemetries[0].producer;
      }
      return null;
    },

    producerName() {
      return this.producer ? this.$filters.capitalize(this.producer.display_name || "") : "";
    },

    ranges() {
      const h = this.$t("limitations.duration_units.hour");
      const d = this.$t("limitations.duration_units.day");
      return [
        { value: "now-24h", label: `24 ${h}` },
        { value: "now-7d", label: `7 ${d}` },
        { value: "now-30d", label: `30 ${d}` }
      ];
    },

    rangeLabel() {
      const r = this.ranges.find((x) => x.value === this.range);
      return r ? r.label : "";
    },

    headers() {
      const all = [
        { title: this.$filters.capitalize(this.$t("generic.date")), key: "time", sortable: true },
        { title: this.$filters.capitalize(this.$t("telemetry.power")), key: "power", sortable: true, align: "end" },
        { title: this.$filters.capitalize(this.$t("production.injection")), key: "injection", sortable: true, align: "end" },
        { title: this.$filters.capitalize(this.$t("telemetry.radiance_vertical")), key: "radiance_vert", sortable: true, align: "end" },
        { title: this.$filters.capitalize(this.$t("telemetry.radiance_horizontal")), key: "radiance_hor", sortable: true, align: "end" }
      ];
      if (this.$vuetify.display.smAndDown) {
        return all.filter((h) => ["time", "power", "injection"].includes(h.key));
      }
      return all;
    }
  },

  methods: {
    grafanaSolarProducerUrl,
    grafanaSolarProducerPanelUrl,

    hasValue(value) {
      return !isNone(value) && value !== "";
    },

    loadFactor(status) {
      const capacity = Number(status.producer ? status.producer.capacity_solar_panel : 0);
      if (!capacity || capacity <= 0) {
        return null;
      }
      return Math.max(0, (Number(status.power) || 0) / capacity);
    },

    isBelowExpected(status) {
      if (!status.time || !this.hasValue(status.performance_ratio_actual_month_min)) {
        return false;
      }
      return Number(status.power) < Number(status.performance_ratio_actual_month_min);
    },

    relativeTime(value) {
      const minutes = Math.max(0, Math.round(moment().diff(moment(value), "minutes")));
      if (minutes < 60) return this.$t("production.minutes_ago", { n: minutes });
      const hours = Math.round(minutes / 60);
      if (hours < 48) return this.$t("production.hours_ago", { n: hours });
      return this.$t("production.days_ago", { n: Math.round(hours / 24) });
    },

    freshnessClass(last) {
      if (isNone(last)) return "over_5h";
      const age = moment().diff(moment(last), "hours", true);
      if (age >= 5) return "over_5h";
      if (age >= 3) return "over_3h";
      if (age >= 1) return "over_1h";
      return "";
    },

    loadStatus() {
      return this.$store
        .dispatch("telemetries/solarCurrent_producerItems", { ids: [this.producer_id] })
        .then((items) => {
          this.status = Array.isArray(items) && items.length ? items[0] : null;
        })
        .catch((error) => {
          console.error("solar status:", error);
          this.status = null;
        });
    },

    loadTelemetries() {
      this.telemetriesLoading = true;
      return this.$store
        .dispatch("telemetries/solarLatestTelemetries", { producer_id: this.producer_id, hours: RANGE_HOURS[this.range] || 24 })
        .then((items) => {
          this.telemetries = items;
        })
        .catch((error) => {
          console.error("solar telemetries:", error);
          this.telemetries = [];
        })
        .finally(() => {
          this.telemetriesLoading = false;
        });
    },

    load() {
      if (isNone(this.producer_id)) {
        this.status = null;
        this.telemetries = [];
        return;
      }
      this.loading = true;
      Promise.allSettled([this.loadStatus(), this.loadTelemetries()]).then(() => {
        this.loading = false;
        this.lastRefresh = moment();
      });
    },

    download(range) {
      if (!range || isNone(this.producer_id)) {
        return;
      }
      this.$store.dispatch("onLoading");
      this.getFile("telemetries/solar/producer/export_csv/", "", "text/csv", "blob", {
        start: range.start,
        end: range.end,
        producer_id: this.producer_id
      });
      this.showExport = false;
    }
  },

  watch: {
    producer_id() {
      this.load();
    }
  },

  mounted() {
    this.load();
    this.intervalId = window.setInterval(() => {
      this.load();
    }, 10 * 60 * 1000);
  },

  unmounted() {
    window.clearInterval(this.intervalId);
  },

  data() {
    return {
      intervalId: undefined,
      loading: false,
      telemetriesLoading: false,
      lastRefresh: null,
      range: "now-24h",
      status: null,
      telemetries: [],
      showExport: false,
      GRAFANA_SOLAR_PANELS,
      GRAFANA_EMBED_ENABLED
    };
  }
};
</script>

<style scoped>
.over_1h {
  color: #c9a800;
}
.over_3h {
  color: #e07b00;
}
.over_5h {
  color: #d32f2f;
}
.fresh {
  color: #2e7d32;
}
.fresh-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  background: color-mix(in srgb, currentColor 12%, white);
}

/* Sticky header, pinned under the app bar while the page scrolls */
.topbar {
  position: sticky;
  top: var(--v-layout-top, 0px);
  z-index: 5;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.topbar::before {
  content: "";
  display: block;
  height: 3px;
  background: linear-gradient(90deg, #0093bf 0%, #15a9c9 55%, #f6b73c 100%);
}
.topbar__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px 10px 8px;
}
.topbar__badge {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff3d6;
  color: #e08a00;
}
.topbar__badge--on {
  background: #e3f4e4;
  color: #2e7d32;
}
.topbar__badge--off {
  background: #fde7e7;
  color: #d32f2f;
}
.topbar__title {
  flex: 1 1 200px;
}
.topbar__name {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar__sub {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
}
.range-toggle {
  border-color: rgba(0, 0, 0, 0.12);
}
.topbar__progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}
.min-width-0 {
  min-width: 0;
}

/* KPI tiles */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}
.kpi {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.kpi__badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi--amber .kpi__badge {
  background: #fff3d6;
  color: #e08a00;
}
.kpi--teal .kpi__badge {
  background: #dcf5ef;
  color: #00897b;
}
.kpi--yellow .kpi__badge {
  background: #fff8dc;
  color: #c9a800;
}
.kpi--blue .kpi__badge {
  background: #dff3f9;
  color: #0093bf;
}
.kpi__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kpi__value {
  margin-top: 2px;
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.87);
}
.kpi__value--small {
  font-size: 1rem;
}
.kpi__unit {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
}
.kpi__hint {
  margin-top: 2px;
  min-height: 1.1em;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Grafana panels */
.graf-card {
  padding: 12px 16px 10px;
  height: 100%;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.graf-card__head {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}
.graf-frame {
  display: block;
  width: 100%;
  height: 320px;
  border: 0;
  border-radius: 8px;
  background: #fafbfc;
}
.graf-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 140px;
  border-radius: 8px;
  border: 1px dashed rgba(0, 147, 191, 0.4);
  background: #f7fbfd;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
  text-decoration: none;
}
.graf-placeholder:hover {
  background: #eef7fb;
}

/* Telemetry table */
.table-card {
  margin-top: 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}
.table-card__head {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: #fafbfc;
}
.tel-table :deep(th) {
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.55) !important;
  background: #fff;
}
.tel-table :deep(td) {
  font-variant-numeric: tabular-nums;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
}
.tel-table :deep(tbody tr:hover td) {
  background-color: #f2f9fc;
}
.empty-card {
  margin-top: 10px;
  padding: 32px 16px;
  text-align: center;
  border-radius: 12px;
  border: 1px dashed rgba(0, 0, 0, 0.12);
}

@media (max-width: 900px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .topbar__row {
    flex-wrap: wrap;
  }
  .topbar__actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
  }
}

@media (max-width: 700px) {
  .graf-frame {
    height: 220px !important;
  }
  .topbar__actions {
    flex-wrap: wrap;
    gap: 6px;
  }
  .range-toggle {
    width: 100%;
  }
  .range-toggle .v-btn {
    flex: 1;
  }
}
</style>
